import React, { useContext, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiRefreshCw,
  FiUsers,
  FiMessageSquare,
  FiCheckCircle,
  FiActivity,
  FiAward,
  FiTrendingUp,
  FiTrendingDown,
  FiTarget,
  FiCalendar,
  FiClock,
  FiFolder,
  FiFileText,
  FiAlertCircle,
  FiUserPlus,
  FiCheck,
  FiLock,
} from "react-icons/fi";
import { UserContext } from "../../context/UserContext";
import { useRoom } from "../../context/RoomContext";
import { useRoomPreview } from "../../hooks/useRoomPreview";
import { getFieldImage } from "../../utils/roomHelpers";
import "./RoomPreview.css";

// ==============================================
// Animation Variants
// ==============================================
const pageVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3, staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const widgetVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 300, damping: 25 } },
};

// ==============================================
// Helper Functions
// ==============================================
const formatDate = (dateString) => {
  if (!dateString) return "--";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ==============================================
// Loading Skeleton
// ==============================================
const LoadingSkeleton = () => (
  <div className="rpv-page">
    <div className="rpv-container">
      <div className="rpv-loading-page">
        <div className="rpv-loading-header">
          <div className="rpv-skeleton rpv-loading-avatar" />
          <div className="rpv-loading-content">
            <div className="rpv-skeleton rpv-loading-title" />
            <div className="rpv-skeleton rpv-loading-text" />
            <div className="rpv-skeleton rpv-loading-text" style={{ width: "40%" }} />
          </div>
        </div>
        <div className="rpv-loading-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="rpv-skeleton rpv-loading-widget" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ==============================================
// Error State
// ==============================================
const ErrorState = ({ error, onRetry }) => (
  <div className="rpv-page">
    <div className="rpv-container">
      <div className="rpv-error">
        <div className="rpv-error-icon">
          <FiAlertCircle />
        </div>
        <h2 className="rpv-error-title">Failed to Load Preview</h2>
        <p className="rpv-error-message">{error || "Something went wrong. Please try again."}</p>
        <motion.button
          className="rpv-error-btn"
          onClick={onRetry}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Try Again
        </motion.button>
      </div>
    </div>
  </div>
);

// ==============================================
// Health Score Widget
// ==============================================
const HealthScoreWidget = ({ health }) => {
  if (!health) return null;

  const circumference = 2 * Math.PI * 45;
  const progress = (health.score / 100) * circumference;

  return (
    <motion.div className="rpv-widget" variants={widgetVariants}>
      <div className="rpv-widget-header">
        <div className="rpv-widget-icon rpv-widget-icon--amber">
          <FiAward />
        </div>
        <h3 className="rpv-widget-title">Lab Health Score</h3>
      </div>
      <div className="rpv-widget-body">
        <div className="rpv-health-main">
          <div className="rpv-health-circle">
            <svg width="110" height="110" viewBox="0 0 110 110">
              <circle className="rpv-health-bg" cx="55" cy="55" r="45" />
              <circle
                className={`rpv-health-progress rpv-health-progress--${health.statusColor}`}
                cx="55"
                cy="55"
                r="45"
                strokeDasharray={`${progress} ${circumference}`}
              />
            </svg>
            <div className="rpv-health-value">
              <span className="rpv-health-score">{health.score}</span>
              <span className="rpv-health-max">/ 100</span>
            </div>
          </div>
          <div className="rpv-health-info">
            <div className={`rpv-health-status rpv-health-status--${health.statusColor}`}>
              <FiActivity size={14} />
              {health.status}
            </div>
            <div className="rpv-health-breakdown">
              {Object.entries(health.breakdown || {}).map(([key, value]) => (
                <div key={key} className="rpv-breakdown-item">
                  <span className="rpv-breakdown-label">{key}</span>
                  <div className="rpv-breakdown-bar">
                    <div
                      className={`rpv-breakdown-fill rpv-breakdown-fill--${health.statusColor}`}
                      style={{ width: `${(value.score / value.maxScore) * 100}%` }}
                    />
                  </div>
                  <span className="rpv-breakdown-score">
                    {value.score}/{value.maxScore}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {health.suggestions && health.suggestions.length > 0 && (
          <div className="rpv-health-suggestions">
            <div className="rpv-suggestions-title">
              💡 Suggestions
            </div>
            <ul className="rpv-suggestions-list">
              {health.suggestions.map((suggestion, idx) => (
                <li key={idx}>{suggestion}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ==============================================
// Activity Widget
// ==============================================
const ActivityWidget = ({ activity }) => {
  if (!activity) return null;

  const maxCount = Math.max(...(activity.dailyActivity?.map((d) => d.count) || [1]));

  return (
    <motion.div className="rpv-widget" variants={widgetVariants}>
      <div className="rpv-widget-header">
        <div className="rpv-widget-icon rpv-widget-icon--blue">
          <FiActivity />
        </div>
        <h3 className="rpv-widget-title">Activity Overview</h3>
      </div>
      <div className="rpv-widget-body">
        <div className="rpv-activity-stats">
          <div className="rpv-stat-card rpv-stat-card--blue">
            <div className="rpv-stat-value rpv-stat-value--blue">
              {activity.totalMessages?.toLocaleString() || 0}
            </div>
            <div className="rpv-stat-label">Total Messages</div>
          </div>
          <div className="rpv-stat-card rpv-stat-card--green">
            <div className="rpv-stat-value rpv-stat-value--green">
              {activity.avgMessagesPerDay || 0}
            </div>
            <div className="rpv-stat-label">Avg/Day</div>
          </div>
        </div>

        <div className="rpv-trend-row">
          <span className="rpv-trend-label">This Week</span>
          <div className={`rpv-trend-value rpv-trend-value--${activity.messagesTrendDirection || 'up'}`}>
            {activity.messagesTrendDirection === "up" ? (
              <FiTrendingUp size={16} />
            ) : (
              <FiTrendingDown size={16} />
            )}
            {activity.messagesTrend > 0 ? "+" : ""}
            {activity.messagesTrend || 0}%
          </div>
        </div>

        <div className="rpv-peak-times">
          <div className="rpv-peak-item">
            <span className="rpv-peak-label">Peak Day</span>
            <span className="rpv-peak-value">{activity.peakActivityDay || "--"}</span>
          </div>
          <div className="rpv-peak-item">
            <span className="rpv-peak-label">Peak Hours</span>
            <span className="rpv-peak-value">{activity.peakActivityHour || "--"}</span>
          </div>
        </div>

        {activity.dailyActivity && activity.dailyActivity.length > 0 && (
          <div className="rpv-activity-chart">
            <div className="rpv-chart-title">Last 7 Days</div>
            <div className="rpv-chart-bars">
              {activity.dailyActivity.slice(-7).map((day, idx) => (
                <div
                  key={idx}
                  className="rpv-chart-bar"
                  style={{
                    height: `${Math.max(8, (day.count / maxCount) * 100)}%`,
                  }}
                  title={`${day.date}: ${day.count} messages`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ==============================================
// Tasks Widget
// ==============================================
const TasksWidget = ({ tasks }) => {
  if (!tasks) return null;

  return (
    <motion.div className="rpv-widget" variants={widgetVariants}>
      <div className="rpv-widget-header">
        <div className="rpv-widget-icon rpv-widget-icon--purple">
          <FiTarget />
        </div>
        <h3 className="rpv-widget-title">Task Analytics</h3>
      </div>
      <div className="rpv-widget-body">
        <div className="rpv-tasks-overview">
          <div className="rpv-task-stat">
            <div className="rpv-task-stat-value">{tasks.total || 0}</div>
            <div className="rpv-task-stat-label">Total</div>
          </div>
          <div className="rpv-task-stat rpv-task-stat--completed">
            <div className="rpv-task-stat-value rpv-task-stat-value--green">
              {tasks.byStatus?.completed?.count || 0}
            </div>
            <div className="rpv-task-stat-label">Done</div>
          </div>
          <div className="rpv-task-stat rpv-task-stat--rate">
            <div className="rpv-task-stat-value rpv-task-stat-value--blue">
              {tasks.completionRate || 0}%
            </div>
            <div className="rpv-task-stat-label">Rate</div>
          </div>
        </div>

        <div className="rpv-status-list">
          {["in-progress", "review", "blocked", "overdue"].map((status) => (
            <div key={status} className="rpv-status-item">
              <span className="rpv-status-name">{status.replace("-", " ")}</span>
              <span className={`rpv-status-count rpv-status-count--${status.replace("-", "")}`}>
                {tasks.byStatus?.[status]?.count || 0}
              </span>
            </div>
          ))}
        </div>

        <div className="rpv-tasks-info">
          <div className="rpv-info-row">
            <span className="rpv-info-label">Due Soon (7 days)</span>
            <span className="rpv-info-value">{tasks.tasksDueSoon || 0}</span>
          </div>
          <div className="rpv-info-row">
            <span className="rpv-info-label">Avg Completion</span>
            <span className="rpv-info-value">{tasks.avgCompletionTime || 0} days</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ==============================================
// Contributors Widget
// ==============================================
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const ContributorsWidget = ({ engagement }) => {
  if (!engagement) return null;

  return (
    <motion.div className="rpv-widget" variants={widgetVariants}>
      <div className="rpv-widget-header">
        <div className="rpv-widget-icon rpv-widget-icon--green">
          <FiUsers />
        </div>
        <h3 className="rpv-widget-title">Top Contributors</h3>
      </div>
      <div className="rpv-widget-body">
        {(!engagement.topContributors || engagement.topContributors.length === 0) ? (
          <div className="rpv-empty-state">
            <FiUsers size={24} style={{ marginBottom: 8, opacity: 0.5 }} />
            <div>No activity yet</div>
          </div>
        ) : (
          <div className="rpv-contributors-list">
            {engagement.topContributors.slice(0, 5).map((contributor, idx) => {
              const hasPhoto = contributor.user?.profilePhotoUrl;
              const initials = getInitials(contributor.user?.name);
              
              return (
                <div key={idx} className="rpv-contributor">
                  <div className="rpv-contributor-avatar">
                    {hasPhoto ? (
                      <img
                        src={contributor.user.profilePhotoUrl}
                        alt={contributor.user?.name}
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="rpv-contributor-initials"
                      style={{ display: hasPhoto ? 'none' : 'flex' }}
                    >
                      {initials}
                    </div>
                    {idx < 3 && (
                      <span className={`rpv-contributor-rank rpv-contributor-rank--${idx + 1}`}>
                        {idx + 1}
                      </span>
                    )}
                  </div>
                  <div className="rpv-contributor-info">
                    <div className="rpv-contributor-name">{contributor.user?.name || "Unknown"}</div>
                    <div className="rpv-contributor-stats">
                      {contributor.messages || 0} msgs • {contributor.tasks || 0} tasks
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="rpv-activity-summary">
          <div className="rpv-summary-row">
            <span className="rpv-summary-label">Active this week</span>
            <span className="rpv-summary-value">
              {engagement.memberActivity?.activeThisWeek || 0} members
            </span>
          </div>
          <div className="rpv-summary-row">
            <span className="rpv-summary-label">Avg response time</span>
            <span className="rpv-summary-value">{engagement.avgResponseTime || "--"}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ==============================================
// Next Meeting Widget
// ==============================================
const NextMeetingWidget = ({ calendar }) => {
  if (!calendar) return null;

  const meeting = calendar.nextMeeting;

  return (
    <motion.div className="rpv-widget" variants={widgetVariants}>
      <div className="rpv-widget-header">
        <div className="rpv-widget-icon rpv-widget-icon--pink">
          <FiCalendar />
        </div>
        <h3 className="rpv-widget-title">Next Meeting</h3>
      </div>
      <div className="rpv-widget-body">
        {!meeting ? (
          <div className="rpv-meeting-empty">
            <FiCalendar size={32} style={{ marginBottom: 8 }} />
            <div>No upcoming meetings scheduled</div>
          </div>
        ) : (
          <div className="rpv-meeting-card">
            <div className="rpv-meeting-title">{meeting.title}</div>
            <div className="rpv-meeting-type">{meeting.eventType}</div>
            <div className="rpv-meeting-time">
              <FiClock size={14} />
              {formatDate(meeting.date)} at {formatTime(meeting.date)}
            </div>
            {meeting.location && (
              <div className="rpv-meeting-location">📍 {meeting.location}</div>
            )}
            {(meeting.meetingLinks?.zoom?.url || meeting.meetingLinks?.googleMeet?.url) && (
              <a
                href={meeting.meetingLinks?.zoom?.url || meeting.meetingLinks?.googleMeet?.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rpv-meeting-join"
              >
                Join Meeting
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ==============================================
// Resources Widget
// ==============================================
const ResourcesWidget = ({ resources, files }) => {
  if (!resources && !files) return null;

  return (
    <motion.div className="rpv-widget" variants={widgetVariants}>
      <div className="rpv-widget-header">
        <div className="rpv-widget-icon rpv-widget-icon--indigo">
          <FiFolder />
        </div>
        <h3 className="rpv-widget-title">Resources & Files</h3>
      </div>
      <div className="rpv-widget-body">
        <div className="rpv-resources-grid">
          <div className="rpv-resource-stat rpv-resource-stat--blue">
            <div className="rpv-resource-value rpv-resource-value--blue">
              {resources?.totalResources || 0}
            </div>
            <div className="rpv-resource-label">Resources</div>
          </div>
          <div className="rpv-resource-stat rpv-resource-stat--purple">
            <div className="rpv-resource-value rpv-resource-value--purple">
              {files?.totalFiles || 0}
            </div>
            <div className="rpv-resource-label">Files</div>
          </div>
        </div>

        <div className="rpv-resource-types">
          {Object.entries(resources?.byType || {}).slice(0, 4).map(([type, count]) => (
            <div key={type} className="rpv-resource-type">
              <span className="rpv-type-name">{type}s</span>
              <span className="rpv-type-count">{count}</span>
            </div>
          ))}
        </div>

        <div className="rpv-storage-info">
          <div className="rpv-storage-row">
            <span className="rpv-storage-label">Total storage</span>
            <span className="rpv-storage-value">{files?.totalSize || "0 KB"}</span>
          </div>
          <div className="rpv-storage-row">
            <span className="rpv-storage-label">Added this week</span>
            <span className="rpv-storage-value rpv-storage-value--green">
              +{resources?.recentlyAdded || 0}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ==============================================
// Calendar Stats Widget
// ==============================================
const CalendarStatsWidget = ({ calendar }) => {
  if (!calendar) return null;

  return (
    <motion.div className="rpv-widget" variants={widgetVariants}>
      <div className="rpv-widget-header">
        <div className="rpv-widget-icon rpv-widget-icon--cyan">
          <FiCalendar />
        </div>
        <h3 className="rpv-widget-title">Calendar Overview</h3>
      </div>
      <div className="rpv-widget-body">
        <div className="rpv-calendar-stats">
          <div className="rpv-calendar-stat">
            <div className="rpv-calendar-value">{calendar.totalEvents || 0}</div>
            <div className="rpv-calendar-label">Total Events</div>
          </div>
          <div className="rpv-calendar-stat">
            <div className="rpv-calendar-value">{calendar.upcomingEvents || 0}</div>
            <div className="rpv-calendar-label">Upcoming</div>
          </div>
        </div>

        <div className="rpv-event-types">
          {Object.entries(calendar.byType || {}).map(([type, count]) => (
            <div key={type} className="rpv-event-type">
              <span className="rpv-event-name">{type}s</span>
              <span className="rpv-event-count">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ==============================================
// Timeline Widget
// ==============================================
const TimelineWidget = ({ timeline }) => {
  if (!timeline || !timeline.recentHighlights?.length) return null;

  const getIconClass = (type) => {
    switch (type) {
      case "task_completed": return "rpv-timeline-icon--task";
      case "resource_added": return "rpv-timeline-icon--resource";
      case "event": return "rpv-timeline-icon--event";
      default: return "rpv-timeline-icon--task";
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "task_completed": return <FiCheckCircle />;
      case "resource_added": return <FiFileText />;
      case "event": return <FiCalendar />;
      default: return <FiActivity />;
    }
  };

  return (
    <motion.div className="rpv-widget" variants={widgetVariants}>
      <div className="rpv-widget-header">
        <div className="rpv-widget-icon rpv-widget-icon--indigo">
          <FiActivity />
        </div>
        <h3 className="rpv-widget-title">Recent Highlights</h3>
      </div>
      <div className="rpv-widget-body">
        <div className="rpv-timeline-list">
          {timeline.recentHighlights.slice(0, 5).map((item, idx) => (
            <div key={idx} className="rpv-timeline-item">
              <div className={`rpv-timeline-icon ${getIconClass(item.type)}`}>
                {getIcon(item.type)}
              </div>
              <div className="rpv-timeline-content">
                <div className="rpv-timeline-title">{item.title}</div>
                <div className="rpv-timeline-date">{formatDate(item.date)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ==============================================
// Quick Stats Bar
// ==============================================
const QuickStatsBar = ({ room, quickStats, health }) => (
  <motion.div className="rpv-quick-stats" variants={itemVariants}>
    <div className="rpv-quick-stat">
      <div className="rpv-quick-icon rpv-widget-icon--blue">
        <FiUsers />
      </div>
      <div className="rpv-quick-value">{room?.memberCount || 0}/8</div>
      <div className="rpv-quick-label">Members</div>
    </div>
    <div className="rpv-quick-stat">
      <div className="rpv-quick-icon rpv-widget-icon--green">
        <FiMessageSquare />
      </div>
      <div className="rpv-quick-value">{quickStats?.totalMessages || 0}</div>
      <div className="rpv-quick-label">Messages</div>
    </div>
    <div className="rpv-quick-stat">
      <div className="rpv-quick-icon rpv-widget-icon--purple">
        <FiCheckCircle />
      </div>
      <div className="rpv-quick-value">
        {quickStats?.completedTasks || 0}/{quickStats?.totalTasks || 0}
      </div>
      <div className="rpv-quick-label">Tasks Done</div>
    </div>
    <div className="rpv-quick-stat">
      <div className="rpv-quick-icon rpv-widget-icon--amber">
        <FiAward />
      </div>
      <div className="rpv-quick-value">{health?.score || 0}</div>
      <div className="rpv-quick-label">Health</div>
    </div>
  </motion.div>
);

// ==============================================
// Header Action Button
// ==============================================
const ActionButton = ({ userRelationship, room, onJoinRequest }) => {
  if (userRelationship?.isMember) {
    return (
      <motion.button
        className="rpv-action-btn rpv-action-btn--primary"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => window.location.href = `/labs/${room._id}`}
      >
        <FiCheck size={16} />
        Open Lab
      </motion.button>
    );
  }

  if (userRelationship?.hasRequestedToJoin) {
    return (
      <button className="rpv-action-btn rpv-action-btn--secondary" disabled>
        <FiClock size={16} />
        Request Pending
      </button>
    );
  }

  if (room?.spotsAvailable > 0) {
    return (
      <motion.button
        className="rpv-action-btn rpv-action-btn--success"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onJoinRequest}
      >
        <FiUserPlus size={16} />
        Request to Join
      </motion.button>
    );
  }

  return (
    <button className="rpv-action-btn rpv-action-btn--secondary" disabled>
      <FiLock size={16} />
      Lab Full
    </button>
  );
};

// ==============================================
// Main Component
// ==============================================
const RoomPreviewPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  useContext(UserContext); // For auth context
  const { requestJoin } = useRoom();

  const { preview, loading, error, isRefreshing, refresh, refetch } = useRoomPreview(roomId);
  
  // State for expanded description
  const [descExpanded, setDescExpanded] = useState(false);

  // Handle join request
  const handleJoinRequest = async () => {
    if (!roomId) return;
    try {
      await requestJoin(roomId);
      refetch();
    } catch (err) { /* ignored */ }
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error || !preview) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  const {
    room,
    activity,
    tasks,
    resources,
    files,
    calendar,
    engagement,
    health,
    timeline,
    userRelationship,
  } = preview;

  return (
    <motion.div
      className="rpv-page"
      variants={pageVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <header className="rpv-header">
        <div className="rpv-container">
          <motion.button
            className="rpv-back-btn"
            onClick={() => navigate(-1)}
            whileHover={{ x: -3 }}
          >
            <FiArrowLeft />
            Back to Labs
          </motion.button>

          <div className="rpv-header-inner">
            <motion.div className="rpv-header-image" variants={itemVariants}>
              <img
                src={room?.photoToUse || room?.signedProfilePhotoUrl || room?.photoURL || getFieldImage(room?.field)}
                alt={room?.name}
                onError={(e) => { 
                  // Fallback chain: photoURL -> field image -> default
                  if (e.target.src !== room?.photoURL && room?.photoURL) {
                    e.target.src = room.photoURL;
                  } else {
                    e.target.src = getFieldImage(room?.field) || "/field-images/default.jpg";
                  }
                }}
              />
            </motion.div>

            <motion.div className="rpv-header-content" variants={itemVariants}>
              {/* Badges + Action Button on same row */}
              <div className="rpv-header-badges">
                <span className="rpv-badge rpv-badge-field">{room?.field}</span>
                {room?.subField && (
                  <span className="rpv-badge rpv-badge-subfield">{room?.subField}</span>
                )}
                {room?.spotsAvailable > 0 && (
                  <span className="rpv-badge" style={{ background: "#d1fae5", color: "#059669" }}>
                    {room.spotsAvailable} spots
                  </span>
                )}
                {/* Action button inline on mobile */}
                <span className="rpv-header-actions-inline">
                  <ActionButton
                    userRelationship={userRelationship}
                    room={room}
                    onJoinRequest={handleJoinRequest}
                  />
                </span>
              </div>

              <h1 className="rpv-header-title">{room?.name}</h1>
              
              {/* Expandable description */}
              <div className={`rpv-header-desc ${descExpanded ? 'rpv-desc-expanded' : ''}`}>
                <p>{room?.description}</p>
                {room?.description && room.description.length > 80 && (
                  <button 
                    className="rpv-see-more-btn"
                    onClick={() => setDescExpanded(!descExpanded)}
                  >
                    {descExpanded ? 'See less' : 'See more'}
                  </button>
                )}
              </div>

              <div className="rpv-header-meta">
                <span className="rpv-meta-item">
                  <FiUsers size={14} />
                  {room?.memberCount || 0}/8 members
                </span>
                <span className="rpv-meta-divider" />
                <span className="rpv-meta-item">
                  <FiActivity size={14} />
                  {room?.daysActive || 0} days active
                </span>
                <span className="rpv-meta-divider" />
                <span className="rpv-meta-item">
                  Led by {room?.leader?.name || "Unknown"}
                </span>
              </div>
            </motion.div>

            {/* Desktop only: Refresh button */}
            <motion.div className="rpv-header-actions rpv-desktop-only" variants={itemVariants}>
              <motion.button
                className={`rpv-refresh-btn ${isRefreshing ? "rpv-spinning" : ""}`}
                onClick={refresh}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Refresh stats"
              >
                <FiRefreshCw size={18} />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="rpv-stats-section">
        <div className="rpv-container">
          {/* Quick Stats Bar */}
          <QuickStatsBar
            room={room}
            quickStats={{
              totalMessages: activity?.totalMessages,
              totalTasks: tasks?.total,
              completedTasks: tasks?.byStatus?.completed?.count,
            }}
            health={health}
          />

          {/* Stats Grid */}
          <motion.div className="rpv-stats-grid" variants={pageVariants}>
            <HealthScoreWidget health={health} />
            <ActivityWidget activity={activity} />
            <TasksWidget tasks={tasks} />
            <NextMeetingWidget calendar={calendar} />
            <ContributorsWidget engagement={engagement} />
            <ResourcesWidget resources={resources} files={files} />
            <CalendarStatsWidget calendar={calendar} />
          </motion.div>

          {/* Timeline Section */}
          {timeline?.recentHighlights?.length > 0 && (
            <motion.div
              className="rpv-stats-grid"
              style={{ marginTop: "16px", gridTemplateColumns: "1fr" }}
              variants={pageVariants}
            >
              <TimelineWidget timeline={timeline} />
            </motion.div>
          )}
        </div>
      </section>
    </motion.div>
  );
};

export default RoomPreviewPage;
